//client\src\calendar\helpers\selectors\CalendarEvents.js

import dayjs from "dayjs";
import { getTrackerEvents } from "../getTrackerEvents";
import { formatISO } from "../../../Utilities/dateUtils";

/**
 * getCalendarEvents
 *
 * Adapter function that converts Talkear calendar-domain state
 * into FullCalendar-compatible event objects.
 *
 * This function is PURE:
 * - no mutations
 * - no side effects
 * - no UI dependencies
 *
 * @param {Object} params
 * @param {Object} params.calendarState - calendar-owned data only
 * @param {Object} params.settings - calendar display + filter settings
 * @param {Object} params.calendarRef - optional calendar ref (used by trackers)
 */
export function getCalendarEvents({
    calendarState = {},
    settings = {},
    calendarRef = null,
}) {
    const {
        tasks = [],
        reminders = [],
        events = [],
        trackers = [],
    } = calendarState;

    const {
        enabledItemTypes = {},
        itemTypeColors = {},
        calendarFilters = {},
    } = settings;

    const format = formatISO;

    // ───────── TASKS ─────────
    const taskEvents =
        calendarFilters.tasks && enabledItemTypes.tasks
            ? tasks.map((task) => ({
                title: `${getPriorityEmoji(task.priority)} ${task.text}`,
                date: format(task.dueDate),
                allDay: true,
                color: itemTypeColors.tasks,
                extendedProps: { ...task, type: "task" },
            }))
            : [];

    // ───────── REMINDERS ─────────
    const reminderEvents =
        calendarFilters.reminders && enabledItemTypes.reminders
            ? reminders.flatMap((r) => {
                const baseDate = format(r.date);

                const isValidTime =
                    r.time &&
                    typeof r.time === "string" &&
                    dayjs(
                        `${baseDate}T${r.time}`,
                        "YYYY-MM-DDTHH:mm",
                        true
                    ).isValid();

                if (isValidTime) {
                    const start = `${baseDate}T${r.time}:00`;

                    return [
                        {
                            title: `⏰ ${dayjs(start).format("h:mm A")} ${r.title}`,
                            start,
                            allDay: false,
                            color: itemTypeColors.reminders,
                            extendedProps: { ...r, type: "reminder" },
                        },
                    ];
                }

                // Fallback: all-day reminder
                return [
                    {
                        title: `⏰ ${r.title}`,
                        date: baseDate,
                        allDay: true,
                        color: itemTypeColors.reminders,
                        extendedProps: { ...r, type: "reminder" },
                    },
                ];
            })
            : [];

    // ───────── EVENTS & BIRTHDAYS ─────────
    const eventEvents = events
        .filter((ev) =>
            ev.type === "birthday"
                ? calendarFilters.birthdays && enabledItemTypes.birthdays
                : calendarFilters.events && enabledItemTypes.events
        )
        .map((ev) => {
            const isBirthday = ev.type === "birthday";

            const base = {
                title: isBirthday ? `🎂 ${ev.title}` : ev.title,
                color: isBirthday
                    ? itemTypeColors.birthdays
                    : itemTypeColors.events,
                extendedProps: { ...ev, type: ev.type || "event" },
            };

            return ev.startTime
                ? {
                    ...base,
                    start: `${format(ev.date)}T${ev.startTime}`,
                    ...(ev.endTime && {
                        end: `${format(ev.date)}T${ev.endTime}`,
                    }),
                    allDay: false,
                }
                : {
                    ...base,
                    date: format(ev.date),
                    allDay: true,
                };
        });

    // ───────── TRACKERS ─────────
    const trackerEvents = getTrackerEvents({
        trackers,
        settings,
        calendarFilters,
        enabledItemTypes,
        itemTypeColors,
        calendarRef,
        format,
    });

    // ───────── COMBINED OUTPUT ─────────
    return [
        ...taskEvents,
        ...reminderEvents,
        ...eventEvents,
        ...trackerEvents,
    ];
}

// ───────── UTILITIES ─────────
function getPriorityEmoji(priority = "") {
    const map = {
        high: "🔴",
        medium: "🟡",
        low: "🟢",
    };
    return map[priority?.toLowerCase?.()] || "";
}
